import { useId, useState } from 'react';
import {
  EmptyState,
  SegmentedControl,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import type { CampaignReport } from '@/lib/types';
import { OPENS_ESTIMATE_REASON } from './ReportStatTiles';

/*
 * Opens and clicks per hour for the first 72 hours after the campaign started.
 * A hand-drawn SVG, no chart library — two series on one axis, opens a solid
 * line in `--chart-1`, clicks a dashed line in `--chart-2`, so the two are
 * told apart by stroke as well as colour. The same buckets are available as a
 * table through the view switch, which is also what a screen reader is given.
 *
 * Buckets are rendered in the order the server sent them; nothing here fills
 * gaps or invents an hour.
 */

type Bucket = CampaignReport['timeline'][number];
type View = 'chart' | 'table';

const VIEW_OPTIONS = [
  { value: 'chart', label: 'Chart' },
  { value: 'table', label: 'Table' },
] as const;

const WIDTH = 720;
const HEIGHT = 220;
const PAD_LEFT = 36;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 28;

function hourLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit' });
}

function niceMax(n: number): number {
  if (n <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(n));
  const step = [1, 2, 2.5, 5, 10].find((m) => m * pow >= n / 4) ?? 10;
  return Math.ceil(n / (step * pow)) * step * pow;
}

function Chart({ buckets, titleId }: { buckets: Bucket[]; titleId: string }) {
  const max = niceMax(Math.max(...buckets.map((b) => Math.max(b.opens, b.clicks))));
  const plotW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const x = (i: number) => PAD_LEFT + (buckets.length === 1 ? plotW / 2 : (i / (buckets.length - 1)) * plotW);
  const y = (v: number) => PAD_TOP + plotH - (v / max) * plotH;
  const line = (key: 'opens' | 'clicks') => buckets.map((b, i) => `${x(i).toFixed(1)},${y(b[key]).toFixed(1)}`).join(' ');
  const ticks = [0, max / 2, max];
  const labelEvery = Math.max(1, Math.ceil(buckets.length / 6));

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      // The viewBox scales the 10-unit labels with the box: at 320px wide they
      // rendered ~4px (unreadable), at a 1100px panel ~15px. A 560px floor keeps
      // them legible (the wrapper scrolls) and a 960px cap stops them ballooning.
      className="block h-auto w-full min-w-[560px] max-w-[960px]"
      role="img"
      aria-labelledby={titleId}
    >
      <title id={titleId}>Opens and clicks per hour, first 72 hours</title>
      {ticks.map((t) => (
        <g key={t}>
          <line
            x1={PAD_LEFT}
            x2={WIDTH - PAD_RIGHT}
            y1={y(t)}
            y2={y(t)}
            stroke="var(--hairline)"
            strokeWidth={1}
          />
          <text x={PAD_LEFT - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="var(--fg-muted)">
            {Number.isInteger(t) ? t : t.toFixed(1)}
          </text>
        </g>
      ))}
      {buckets.map((b, i) =>
        i % labelEvery === 0 ? (
          <text
            key={b.hour}
            x={x(i)}
            y={HEIGHT - 8}
            textAnchor="middle"
            fontSize={10}
            fill="var(--fg-muted)"
          >
            {hourLabel(b.hour)}
          </text>
        ) : null,
      )}
      <polyline
        points={line('opens')}
        fill="none"
        stroke="var(--chart-1)"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      <polyline
        points={line('clicks')}
        fill="none"
        stroke="var(--chart-2)"
        strokeWidth={2}
        strokeDasharray="5 3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Legend() {
  return (
    <ul className="m-0 flex list-none flex-wrap gap-4 p-0 text-xs text-fg-secondary">
      <li className="flex items-center gap-1.5">
        <svg width="20" height="8" aria-hidden="true">
          <line x1="0" x2="20" y1="4" y2="4" stroke="var(--chart-1)" strokeWidth="2" />
        </svg>
        <span title={OPENS_ESTIMATE_REASON}>Opens (est.)</span>
      </li>
      <li className="flex items-center gap-1.5">
        <svg width="20" height="8" aria-hidden="true">
          <line x1="0" x2="20" y1="4" y2="4" stroke="var(--chart-2)" strokeWidth="2" strokeDasharray="5 3" />
        </svg>
        <span>Clicks</span>
      </li>
    </ul>
  );
}

function BucketTable({ buckets }: { buckets: Bucket[] }) {
  return (
    <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
      <Table style={{ minWidth: '320px' }}>
        <caption className="sr-only">Opens and clicks per hour</caption>
        <THead>
          <TR>
            <TH scope="col">Hour</TH>
            <TH scope="col" numeric>
              Opens
            </TH>
            <TH scope="col" numeric>
              Clicks
            </TH>
          </TR>
        </THead>
        <TBody>
          {buckets.map((b) => (
            <TR key={b.hour}>
              <TD className="tabular">{formatDateTime(b.hour)}</TD>
              <TD numeric>{b.opens.toLocaleString()}</TD>
              <TD numeric>{b.clicks.toLocaleString()}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}

export function ReportTimeline({ timeline }: { timeline: Bucket[] }) {
  const [view, setView] = useState<View>('chart');
  const titleId = useId();
  const hasActivity = timeline.some((b) => b.opens > 0 || b.clicks > 0);

  if (timeline.length === 0 || !hasActivity) {
    return (
      <EmptyState
        title="No opens or clicks yet"
        description={
          timeline.length === 0
            ? 'The chart starts when the campaign starts sending.'
            : 'Nothing has been opened or clicked in the first 72 hours so far.'
        }
      />
    );
  }

  return (
    <div className="grid min-w-0 gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Legend />
        <SegmentedControl<View>
          label="Show the timeline as"
          size="sm"
          value={view}
          options={VIEW_OPTIONS}
          onChange={setView}
        />
      </div>
      {view === 'chart' ? (
        <>
          <div className="min-w-0 overflow-x-auto">
            <Chart buckets={timeline} titleId={titleId} />
          </div>
          <div className="sr-only">
            <BucketTable buckets={timeline} />
          </div>
        </>
      ) : (
        <BucketTable buckets={timeline} />
      )}
    </div>
  );
}
